import React from 'react';
import clsx from 'clsx';
import type { NavCategory, NavCategoryItem } from './NavCategories';

const SHEET_CATEGORY_IDS = ['connected', 'settings'];
const ALWAYS_VISIBLE_IDS = new Set(['settings', 'explore', 'influencer', 'feedback', 'changelog']);

function isItemVisible(
  item: NavCategoryItem,
  visibleItemIds: Set<string>,
  conditionalFlags: BottomSheetProps['conditionalFlags']
): boolean {
  if (item.conditional === 'coParenting') return conditionalFlags.hasCoParenting;
  if (item.conditional === 'elderCare') return conditionalFlags.hasElderCare;
  if (item.conditional === 'municipal') return conditionalFlags.hasMunicipal;
  return visibleItemIds.has(item.id) || ALWAYS_VISIBLE_IDS.has(item.id);
}

interface BottomSheetProps {
  isOpen: boolean;
  categories: NavCategory[];
  visibleItemIds: Set<string>;
  conditionalFlags: {
    hasCoParenting: boolean;
    hasElderCare: boolean;
    hasMunicipal: boolean;
  };
  activeRoute: string;
  onNavigate: (path: string) => void;
  onClose: () => void;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  categories,
  visibleItemIds,
  conditionalFlags,
  activeRoute,
  onNavigate,
  onClose,
}) => {
  return (
    <>
      {/* Backdrop - only visible when isOpen */}
      <div
        className={clsx(
          'fixed inset-0 z-[55] md:hidden bg-black/40 transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Sheet - always in DOM, animated via transform */}
      <div
        className={clsx(
          'fixed inset-x-0 bottom-0 z-[60] md:hidden bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl border-t border-slate-200 dark:border-slate-800 transition-transform duration-200',
          isOpen ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto mt-3 mb-2" />

        {/* Content */}
        <div className="px-4 pb-8 max-h-[60vh] overflow-y-auto no-scrollbar">
          {categories
            .filter((cat) => SHEET_CATEGORY_IDS.includes(cat.id))
            .map((cat) => {
              const visibleItems = cat.items.filter((item) =>
                isItemVisible(item, visibleItemIds, conditionalFlags)
              );

              if (visibleItems.length === 0) return null;

              return (
                <div key={cat.id}>
                  <p className="px-1 py-2 text-2xs font-black uppercase tracking-widest text-slate-400">
                    {cat.label}
                  </p>
                  <div className="flex flex-col gap-2">
                    {visibleItems.map((item) => {
                      const isActive = activeRoute === item.path;
                      return (
                        <button
                          key={item.id}
                          onClick={() => { onNavigate(item.path); onClose(); }}
                          className={clsx(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold rounded-2xl transition-all',
                            isActive
                              ? 'text-brand bg-brand/10'
                              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                          )}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};
